import { useTranslation } from 'react-i18next';
import { Fragment } from 'react/jsx-runtime';
import { twMerge } from 'tailwind-merge';

import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import Text from '@/components/ui/custom/dataDisplay/Text';
import InfinityList from '@/components/ui/custom/InfinityList';
import { LoadingIcon } from '@/components/icons';
import {
  MANAGEMENT_PAGE_SIZE as pageSize,
  REQUEST_STALE_TIME_IN_MS as staleTime,
} from '@/constants';
import useInfiniteService from '@/data/hook/useInfiniteService';
import { dashboardLessonProgressQueryKeys } from '@/data/services/querykeys';
import { EnrollmentStatusEnum } from '@/models/Enrollment';
import LessonProgressDashboardStats from '@/models/LessonProgressDashboardStats';
import { useLessonsBlock } from '@/pages/admin/courses/studio/CourseStudio.hooks';
import { card } from '@/styles/styles';
import { enrollmentValidation } from '@/utils/enrollmentValidation';
import { ROUTES } from '@/utils/routes';
import ProgressCollectionTitle from '../ProgressCollectionTitle';
import LessonProgressMember from './lessonProgressMember/LessonProgressMember';
import useStudentContext from '@/data/hook/useStudentContext';
import { isBlockedLesson } from '@/utils/isBlockedLesson';

type Props = {
  slugCourseName: string;
  courseProgressId: number;
  enrollmentStatus: EnrollmentStatusEnum;
  klassId?: number;
};

export default function LessonProgressCollection(props: Props) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'lessonProgressCollection',
  });

  return (
    <section className={twMerge(card, 'flex flex-col gap-2.5 p-2 border-none')}>
      <ProgressCollectionTitle title={t('title')} />
      <LessonBlockProgressCollection {...props} />
    </section>
  );
}

export const LessonBlockProgressCollection = (props: Props) => {
  const { slugCourseName } = props;

  const {
    lessonBlocks,
    isInitialLoading: loadingLessonBlocks,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useLessonsBlock(slugCourseName);

  if (loadingLessonBlocks)
    return (
      <span className="flex justify-center items-center">
        <LoadingIcon className="w-10 h-10 text-primary" />
      </span>
    );

  if (!lessonBlocks.length) return <LessonProgressCollectionList {...props} />;

  return (
    <InfinityList
      isFetchingNextPage={isFetchingNextPage}
      hasNextPage={hasNextPage}
      onReachEnd={fetchNextPage}
      className="gap-8 mt-2"
    >
      {lessonBlocks.map(lessonBlock => (
        <div key={lessonBlock.id} className="flex flex-col gap-2">
          <Text
            text={lessonBlock.name}
            format="rubik-500"
            className="text-base-content text-14"
          />
          <LessonProgressCollectionList
            {...props}
            lessonBlockId={lessonBlock.id}
          />
        </div>
      ))}
    </InfinityList>
  );
};

export const LessonProgressCollectionList = (
  props: Props & { lessonBlockId?: number },
) => {
  const {
    courseProgressId,
    slugCourseName,
    klassId,
    enrollmentStatus,
    lessonBlockId,
  } = props;

  const validEnrollment = enrollmentValidation(enrollmentStatus);
  const shouldUseKlassId = validEnrollment && !!klassId;
  const {
    results,
    isInitialLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useInfiniteService({
    ...dashboardLessonProgressQueryKeys.list({
      courseProgressId: [courseProgressId],
      scheduledLessonActive: shouldUseKlassId || undefined,
      lessonBlockId: lessonBlockId ? [lessonBlockId] : undefined,
      orderBy: shouldUseKlassId ? 'scheduled_lesson_order' : 'lesson_order',
      pageSize,
    })._ctx.infinity,
    enabled: !!courseProgressId,
    staleTime,
  });
  return (
    <InfinityList
      className="grid grid-cols-1 gap-2"
      hasNextPage={hasNextPage}
      isFetchingNextPage={isFetchingNextPage}
      onReachEnd={fetchNextPage}
    >
      <ConditionalRenderer
        condition={!isInitialLoading}
        fallback={
          <span className="flex justify-center items-center h-20">
            <LoadingIcon className="w-10 h-10 text-primary" />
          </span>
        }
      >
        {results.map((result, index, array) => (
          <Fragment key={result.id}>
            <Member
              slugCourseName={slugCourseName}
              lessonProgress={result}
              klassId={klassId}
              order={result.lesson.order}
            />
            <ConditionalRenderer condition={index !== array.length - 1}>
              <hr className="border-t border-accent-content h-px" />
            </ConditionalRenderer>
          </Fragment>
        ))}
      </ConditionalRenderer>
    </InfinityList>
  );
};

type MemberProps = {
  order: number | null;
  slugCourseName: string;
  lessonProgress: LessonProgressDashboardStats;
  klassId?: number;
};

function Member(props: MemberProps) {
  const { slugCourseName, lessonProgress, klassId, order } = props;

  const {
    lesson,
    progress,
    activitiesProgress,
    bookProgress,
    challengeProgress,
    hasAccessedTool,
    hasWatchedInstruction,
    presenceStatus,
  } = lessonProgress;

  const to = ROUTES.COURSES.LESSON.INFO(
    slugCourseName,
    lessonProgress.lesson.id,
    klassId,
  );

  const blocked = useBlockedLesson(lessonProgress);

  return (
    <LessonProgressMember
      className={{
        base: 'justify-between flex-col md:flex-row overflow-hidden',
      }}
      disabled={blocked}
    >
      <div className="flex w-full items-center gap-2 overflow-x-hidden">
        <LessonProgressMember.RoundedProgressBar
          banner={lesson.bannerImg}
          progress={progress}
        />

        <LessonProgressMember.Name name={lesson.name} order={order} to={to} />
      </div>
      <div className="flex items-center gap-2 self-end md:self-center">
        <LessonProgressMember.Stats
          activitiesProgress={activitiesProgress}
          bookProgress={bookProgress}
          challengeProgress={challengeProgress}
          hasAccessedTool={hasAccessedTool}
          hasWatchedInstruction={hasWatchedInstruction}
          slugCourseName={slugCourseName}
          lessonId={lesson.id}
          klassId={klassId}
        />
        <LessonProgressMember.Presence
          presence={presenceStatus}
          date={lesson.datetime}
        />
      </div>
    </LessonProgressMember>
  );
}

const useBlockedLesson = (lessonProgress: LessonProgressDashboardStats) => {
  const { nextLesson } = useStudentContext();
  const datetime = lessonProgress.lesson.datetime;
  const presence = lessonProgress.presenceStatus;
  const hasDone = lessonProgress.lesson.hasDone;
  const lessonId = lessonProgress.lesson.id;
  return isBlockedLesson({
    datetime,
    presence,
    lessonId,
    hasDone,
    nextLesson,
  });
};
